<template>
  <div class="sf-sidebar" :class="[staticClass, className]">
    <BaseOverlay :visible="visibleOverlay" />
    <transition :name="transitionName">
      <aside
        v-if="visible"
        ref="asideContent"
        v-focus-trap
        v-click-outside="checkPersistence"
        class="sf-sidebar__aside"
      >
        <slot name="bar">
          <BaseBar :title="title" class="smartphone-only" :back="true" @click:back="close" />
        </slot>
        <slot name="circle-icon" v-bind="{ close, button }">
          <BaseCircleIcon
            :class="{ 'display-none': !button }"
            icon-size="12px"
            aria-label="Close sidebar"
            icon="cross"
            class="sf-sidebar__circle-icon desktop-only"
            @click="close"
          />
        </slot>
        <div :class="{ 'display-none': !title || (!title && !hasTop) }" class="sf-sidebar__top">
          <slot name="title" v-bind="{ title, subtitle, headingLevel }">
            <BaseHeading
              :class="{ 'display-none': !title }"
              :title="title"
              :description="subtitle"
              :level="headingLevel"
              class="sf-heading--left sf-heading--no-underline sf-sidebar__title desktop-only"
            />
          </slot>
          <slot name="content-top" />
        </div>
        <div class="sf-sidebar__content">
          <slot />
        </div>
        <div :class="{ 'display-none': !hasBottom }" class="sf-sidebar__bottom">
          <slot name="content-bottom" />
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import { defineComponent } from '@nuxtjs/composition-api';
import BaseCircleIcon from '@shared/components/@atoms/BaseCircleIcon/BaseCircleIcon.vue';
import BaseHeading from '@shared/components/@atoms/BaseHeading/BaseHeading.vue';
import BaseOverlay from '@shared/components/@atoms/BaseOverlay/BaseOverlay.vue';
import BaseBar from '@shared/components/@molecules/BaseBar/BaseBar.vue';
import { clickOutside, focusTrap } from '@shared/ui/utilities/directives';
import { isClient } from '@shared/ui/utilities/helpers';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';

export default defineComponent({
  name: 'BaseSideBar',
  directives: { focusTrap, clickOutside },
  components: {
    BaseBar,
    BaseCircleIcon,
    BaseOverlay,
    BaseHeading,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    headingLevel: {
      type: Number,
      default: 3,
    },
    button: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    overlay: {
      type: Boolean,
      default: true,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    enableBodyLock: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      position: 'left',
      staticClass: null,
      className: null,
    };
  },
  computed: {
    visibleOverlay() {
      return this.visible && this.overlay;
    },
    transitionName() {
      return `sf-slide-${this.position}`;
    },
    hasTop() {
      // eslint-disable-next-line no-prototype-builtins
      return this.$slots.hasOwnProperty('content-top');
    },
    hasBottom() {
      // eslint-disable-next-line no-prototype-builtins
      return this.$slots.hasOwnProperty('content-bottom');
    },
  },
  watch: {
    visible: {
      handler(value) {
        if (!isClient) return;
        if (value && this.$props.enableBodyLock) {
          this.$nextTick(() => {
            const sidebarContent = document.getElementsByClassName('sf-sidebar__content')[0];
            disableBodyScroll(sidebarContent);
          });
          document.addEventListener('keydown', this.keydownHandler);
        } else {
          clearAllBodyScrollLocks();
          document.removeEventListener('keydown', this.keydownHandler);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.classHandler();
  },
  updated() {
    this.classHandler();
  },
  beforeDestroy() {
    clearAllBodyScrollLocks();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    checkPersistence() {
      if (!this.persistent) this.close();
    },
    keydownHandler(e) {
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        this.close();
      }
    },
    classHandler() {
      let update = false;
      if (this.staticClass !== this.$vnode.data?.staticClass) {
        this.staticClass = this.$vnode.data?.staticClass;
        update = true;
      }
      if (this.className !== this.$vnode.data?.class) {
        this.className = this.$vnode.data?.class;
        update = true;
      }
      if (update) {
        this.position =
          [this.staticClass, this.className].toString().search('--right') > -1 ? 'right' : 'left';
      }
    },
  },
});
</script>

<style lang="scss">
.sf-sidebar {
  &__aside {
    position: fixed;
    top: var(--sidebar-top, 0);
    left: var(--sidebar-left, 0);
    right: var(--sidebar-right, 0);
    bottom: var(--sidebar-bottom, 0);
    z-index: var(--sidebar-z-index, 1);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: var(--sidebar-width);
    height: 100%;
    background-color: var(--sidebar-background, var(--c-white));
    box-shadow: var(--sidebar-box-shadow, 0px 4px 11px rgba(29, 31, 34, 0.1));
  }
  &__top {
    padding: var(--sidebar-top-padding, var(--spacer-sm) var(--spacer-sm) 0 var(--spacer-sm));
    @include font(
      --sidebar-top,
      var(--font-weight--light),
      var(--font-size--base),
      1.6,
      var(--font-family--primary)
    );
  }
  &__content {
    overflow: auto;
    display: var(--sidebar-content-display, flex);
    flex-direction: var(--sidebar-content-flex-direction, column);
    box-sizing: border-box;
    width: var(--sidebar-content-width);
    height: 100%;
    scrollbar-color: var(--c-gray-variant) var(--c-light);
    scrollbar-width: thin;
    padding: var(--sidebar-content-padding, var(--spacer-base) var(--spacer-sm));
    color: var(--sidebar-content-color, var(--c-text));
    @include font(
      --sidebar-content-font,
      var(--font-weight--medium),
      var(--font-size--base),
      1.6,
      var(--font-family--secondary)
    );
    &::-webkit-scrollbar {
      width: 0;
    }
  }
  &__title {
    --heading-title-color: var(--c-dark-variant);
    --heading-title-font-size: var(--font-size--xl);
    --heading-title-font-weight: var(--font-weight--semibold);
    --heading-description-font-size: var(--font-size--xl);
    display: var(--sidebar-title-display);
    margin: var(--sidebar-title, 0 0 var(--spacer-lg) 0);
  }
  &__bottom {
    position: sticky;
    top: 0;
    bottom: auto;
    padding: var(--sidebar-bottom-padding, var(--spacer-sm));
    @include font(
      --sidebar-bottom,
      var(--font-weight--light),
      var(--font-size--base),
      1.6,
      var(--font-family--primary)
    );
  }
  &__circle-icon {
    --circle-icon-position: absolute;
    z-index: 1;
    display: var(--sidebar-circle-icon-display, flex);
    left: var(--sidebar-circle-icon-left);
    top: var(--sidebar-circle-icon-top, var(--spacer-xl));
    right: var(--sidebar-circle-icon-right, 0);
    transform: var(--sidebar-circle-icon-transform, translate3d(50%, 0, 0));
  }
  @include for-desktop {
    --sidebar-width: 25.5rem;
    --sidebar-top-padding: var(--spacer-xl) var(--spacer-xl) var(--spacer-sm) var(--spacer-xl);
    --sidebar-content-padding: var(--spacer-base) var(--spacer-xl);
    --sidebar-bottom-padding: var(--spacer-sm) var(--spacer-xl) var(--spacer-xl) var(--spacer-xl);
    &--right {
      --sidebar-left: auto;
      --sidebar-right: 0;
      --sidebar-top-padding: var(--spacer-base);
      --sidebar-circle-icon-top: var(--spacer-sm);
      --sidebar-content-padding: var(--spacer-base);
      & .sf-sidebar__circle-icon {
        --button-background: transparent;
        --icon-color: var(--c-link);
        &:hover {
          --button-box-shadow-opacity: 0;
        }
      }
      --sidebar-circle-icon-transform: translate3d(-25%, 0, 0);
    }
  }
}
</style>
